import { useNavigateToProject } from '@/Client/State/kaiNavigation';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css } from '@emotion/react';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import {
  AssignedContractorsMap,
  formatCurrency,
  formatDecimal,
  formatTradesForDisplay,
  getBadgeColorScopeState,
  getBadgeTitleScopeState,
  getDocId,
  getReportTitle,
  KAIIZEN_LOGO_SVG,
  REACT_PRINT_STYLES,
  ReportType,
  SCAN_EXPORT_STYLES,
  ScopeReportOverview,
} from '@kaiizen/kai-reports';
import { useState } from 'react';
import { ChevronDownIcon, LockIcon } from '../../Widgets';

const SCOPE_NOTE_STYLE = css`
  margin-bottom: 4px;
  white-space: pre-wrap;
`;

const PAGE_SPACING_STYLE = css`
  margin-bottom: 20px;

  @media print {
    margin-bottom: 0;
  }
`;

const KaiizenLogo = () => <div dangerouslySetInnerHTML={{ __html: KAIIZEN_LOGO_SVG }} />;

const LIVE_BADGE_STYLE = (backgroundColor: string) =>
  css`
  label: LIVE_BADGE_STYLE;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: ${backgroundColor};
  border-radius: 4px;
  font-size: ${pxToRem(12)};
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  text-transform: uppercase;
`;

const HEADER_INFO_CONTAINER_STYLE = css`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: ${pxToRem(14)};
  color: #666;
`;

const INFO_ITEM_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const INFO_LABEL_STYLE = css`
  font-size: ${pxToRem(12)};
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const INFO_VALUE_STYLE = css`
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: #333;
`;

const HEADER_ROW_STYLE = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const HEADER_LEFT_GROUP_STYLE = css`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ORG_NAME_STYLE = css`
  font-size: ${pxToRem(20)};
  font-weight: 700;
  color: #1D1D1D;
  text-align: right;
`;

const DOCUMENT_ID_CONTAINER_STYLE = css`
  text-align: right;
`;

const DOCUMENT_ID_LABEL_STYLE = css`
  font-size: ${pxToRem(12)};
  color: #666;
  margin-bottom: 2px;
`;

const DOCUMENT_ID_VALUE_STYLE = css`
  font-size: ${pxToRem(18)};
  font-weight: 700;
  color: #1D1D1D;
`;

const LAST_UPDATED_STYLE = css`
  font-size: ${pxToRem(14)};
  color: #666;
`;

const SECTION_HEADER_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #E8E8E8;
  padding: 3px 7px;
  margin-bottom: 0;
  gap: 12px;
  font-size: ${pxToRem(18)};
  font-weight: 400;
  color: #1D1D1D;
`;

const SECTION_HEADER_TITLE_STYLE = css`
  flex: 1;
  font-size: ${pxToRem(18)};
  font-weight: 400;
  color: #1D1D1D;
`;

const SECTION_HEADER_ACTIONS_STYLE = css`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EDIT_BUTTON_STYLE = css`
  background-color: white;
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: #1D1D1D;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F5F5F5;
  }

  @media print {
    display: none;
  }
`;

const CHEVRON_BUTTON_STYLE = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1D1D1D;
  transition: transform 0.2s;

  &:hover {
    opacity: 0.7;
  }

  @media print {
    display: none;
  }
`;

const CHEVRON_ROTATED_STYLE = css`
  transform: rotate(180deg);
`;

const COLLAPSIBLE_CONTENT_STYLE = (isCollapsed: boolean | undefined) =>
  css`
  overflow: hidden;
  transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
  max-height: ${isCollapsed ? '0' : '10000px'};
  opacity: ${isCollapsed ? 0 : 1};
`;

export const CoverPage = ({
  overviewData,
  assignedContractorsWithTrades,
  logoSvg,
  reportType = 'fullScope',
  scanLifecycleStatus,
  scanId,
  scanUpdatedAt,
  propertyProjectId,
}: {
  overviewData: ScopeReportOverview;
  assignedContractorsWithTrades: AssignedContractorsMap;
  logoSvg?: string;
  reportType?: ReportType;
  scanLifecycleStatus?: string;
  scanId?: string;
  scanUpdatedAt?: Date;
  propertyProjectId?: string;
}) => {
  const { scanScopeState, firstApprovedCloseoutScan, scan } = useScanEditingContextSlice(['scanScopeState', 'firstApprovedCloseoutScan', 'scan']);
  const [isPropertyDetailsCollapsed, setIsPropertyDetailsCollapsed] = useState(true);
  const [isContractorAssignmentsCollapsed, setIsContractorAssignmentsCollapsed] = useState(true);
  const navigateToProject = useNavigateToProject();
  const isCurrentScope = scanScopeState === 'current';

  const scanIsNewerThanFirstApprovedCloseoutScan = firstApprovedCloseoutScan
    && scan?.createdAt
    && scan.createdAt > firstApprovedCloseoutScan.createdAt;
  const isAddendum = scanIsNewerThanFirstApprovedCloseoutScan
    && scan?.id !== firstApprovedCloseoutScan?.id
    && scan?.parentScanId !== firstApprovedCloseoutScan?.id
    && !isCurrentScope;

  const formattedDate = scanUpdatedAt
    ? new Date(scanUpdatedAt).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    : '';

  const handleEditClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (propertyProjectId) {
      navigateToProject({ projectId: propertyProjectId });
    }
  };

  return (
    <div css={[REACT_PRINT_STYLES.page, PAGE_SPACING_STYLE]}>
      {/* Row 1: Status Badge and Org Name */}
      <div css={[HEADER_ROW_STYLE, css`margin-bottom: 18px;`]}>
        <div>
          {scanLifecycleStatus && (
            <div css={LIVE_BADGE_STYLE(getBadgeColorScopeState(scanLifecycleStatus, reportType, scanScopeState))}>
              <span>{getBadgeTitleScopeState(scanLifecycleStatus, reportType, scanScopeState)}</span>
            </div>
          )}
        </div>
        <div css={ORG_NAME_STYLE}>
          {overviewData?.responsibleOrg || ''}
        </div>
      </div>

      {/* Row 2: Report Title and Document ID */}
      <div css={HEADER_ROW_STYLE}>
        <h1 css={[REACT_PRINT_STYLES.h1, css`display: flex; align-items: center; gap: 8px; line-height: 1;`]}>
          {getReportTitle(reportType, isAddendum)}
          {!(scanScopeState === 'draft') && (
            <span css={css`display: flex; align-items: center;`}>
              <LockIcon color='#1D1D1D' width={20} height={20} />
            </span>
          )}
        </h1>
      </div>

      {/* Row 3: Last Updated */}
      <div css={HEADER_ROW_STYLE}>
        {formattedDate && (
          <div css={LAST_UPDATED_STYLE}>
            Last updated: {formattedDate}
          </div>
        )}
        {scanId && (
          <div css={DOCUMENT_ID_CONTAINER_STYLE}>
            <div css={DOCUMENT_ID_LABEL_STYLE}>Document ID</div>
            <div css={DOCUMENT_ID_VALUE_STYLE}>{getDocId(reportType, scanId)}</div>
          </div>
        )}
      </div>
      {scanScopeState === 'draft'
        ? (
          <div css={SECTION_HEADER_STYLE}>
            <div css={SECTION_HEADER_TITLE_STYLE}>
              {overviewData?.propertyAddress || ''}
            </div>
            <div css={SECTION_HEADER_ACTIONS_STYLE}>
              <button css={EDIT_BUTTON_STYLE} onClick={handleEditClick}>
                EDIT
              </button>
              <button
                css={[CHEVRON_BUTTON_STYLE, !isPropertyDetailsCollapsed && CHEVRON_ROTATED_STYLE]}
                onClick={() => setIsPropertyDetailsCollapsed(!isPropertyDetailsCollapsed)}
                aria-label={isPropertyDetailsCollapsed ? 'Expand property details' : 'Collapse property details'}
              >
                <ChevronDownIcon color='#1D1D1D' width={24} height={24} />
              </button>
            </div>
          </div>
        )
        : (
          <div css={REACT_PRINT_STYLES.headRow}>
            {overviewData?.propertyAddress || ''}
          </div>
        )}

      <div
        css={[
          COLLAPSIBLE_CONTENT_STYLE(scanScopeState === 'draft' ? isPropertyDetailsCollapsed : false),
          REACT_PRINT_STYLES.threeColGrid,
          css`margin-bottom: 20px; padding: 0 10px;`,
        ]}
      >
        <div style={{ paddingRight: '10px' }}>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Beds</div>
              <div>{overviewData?.beds || ''}</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Baths</div>
              <div>{overviewData?.baths || ''}</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Built</div>
              <div>{overviewData?.vintage || ''}</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Acres</div>
              <div>{overviewData?.acres ? formatDecimal(overviewData.acres, 2) : ''}</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Stories</div>
              <div>{overviewData?.stories || ''}</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Listed SqFt</div>
              <div>{overviewData?.squareFeet || ''} ft</div>
            </div>
          </div>
          <div css={REACT_PRINT_STYLES.underlined}>
            <div css={REACT_PRINT_STYLES.rowBetween}>
              <div>Measured SqFt</div>
              <div>{overviewData?.squareFeet || ''} ft</div>
            </div>
          </div>
        </div>

        <div css={REACT_PRINT_STYLES.col10}>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Responsible Org</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.responsibleOrg || ''}</div>
          </div>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Property ID</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.propertyId}</div>
          </div>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Date Approved</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.approvedDate || ''}</div>
          </div>
          <div css={css`display: flex; gap: ${SCAN_EXPORT_STYLES.spacing.small};`}>
            <div css={css`${REACT_PRINT_STYLES.borderBox}; flex: 1;`}>
              <div css={REACT_PRINT_STYLES.borderBoxLabel}>Approved Budget</div>
              <div css={REACT_PRINT_STYLES.borderBoxValue}>{formatCurrency(overviewData?.projectBudget || 0)}</div>
            </div>
            <div css={css`${REACT_PRINT_STYLES.borderBox}; flex: 1;`}>
              <div css={REACT_PRINT_STYLES.borderBoxLabel}>Scoped Total</div>
              <div css={REACT_PRINT_STYLES.borderBoxValue}>{formatCurrency(overviewData?.totalProjectCost || 0)}</div>
            </div>
          </div>
        </div>

        <div css={REACT_PRINT_STYLES.col10}>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Est. Project Completion Date</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.projectEstimatedCompletionDate || ''}</div>
          </div>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Project Name</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.projectName || ''}</div>
          </div>
          <div css={REACT_PRINT_STYLES.borderBox}>
            <div css={REACT_PRINT_STYLES.borderBoxLabel}>Project Type</div>
            <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.scanType || ''}</div>
          </div>

          <div css={css`display: flex; gap: ${SCAN_EXPORT_STYLES.spacing.small};`}>
            <div css={css`${REACT_PRINT_STYLES.borderBox}; flex: 1;`}>
              <div css={REACT_PRINT_STYLES.borderBoxLabel}>Door Code</div>
              <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.doorCode || ''}</div>
            </div>
            <div css={css`${REACT_PRINT_STYLES.borderBox}; flex: 1;`}>
              <div css={REACT_PRINT_STYLES.borderBoxLabel}>Gate Code</div>
              <div css={REACT_PRINT_STYLES.borderBoxValue}>{overviewData?.gateCode || ''}</div>
            </div>
          </div>
        </div>
      </div>
      {Object.keys(assignedContractorsWithTrades || {}).length > 0 && (
        <>
          {scanScopeState === 'draft'
            ? (
              <div css={SECTION_HEADER_STYLE}>
                <div css={SECTION_HEADER_TITLE_STYLE}>
                  Contractor Assignments
                </div>
                <div css={SECTION_HEADER_ACTIONS_STYLE}>
                  <button css={EDIT_BUTTON_STYLE} onClick={handleEditClick}>
                    EDIT
                  </button>
                  <button
                    css={[CHEVRON_BUTTON_STYLE, !isContractorAssignmentsCollapsed && CHEVRON_ROTATED_STYLE]}
                    onClick={() => setIsContractorAssignmentsCollapsed(!isContractorAssignmentsCollapsed)}
                    aria-label={isContractorAssignmentsCollapsed ? 'Expand contractor assignments' : 'Collapse contractor assignments'}
                  >
                    <ChevronDownIcon color='#1D1D1D' width={24} height={24} />
                  </button>
                </div>
              </div>
            )
            : <div css={REACT_PRINT_STYLES.headRow}>Contractor Assignments</div>}
          <div css={[COLLAPSIBLE_CONTENT_STYLE(scanScopeState === 'draft' ? isContractorAssignmentsCollapsed : false), REACT_PRINT_STYLES.threeColGrid]}>
            {Object.entries(assignedContractorsWithTrades || {}).map(([contractorId, contractor]) => (
              <div key={contractorId} css={REACT_PRINT_STYLES.borderBox}>
                <div css={REACT_PRINT_STYLES.borderBoxLabel}>{formatTradesForDisplay(contractor.trades)}</div>
                <div css={REACT_PRINT_STYLES.borderBoxValue}>{contractor.name}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {(overviewData?.scopeNotes?.length ?? 0) > 0 && (
        <>
          <div css={REACT_PRINT_STYLES.headRow}>Scope Notes</div>
          <div css={REACT_PRINT_STYLES.borderBox} data-testid='scope-report-scope-notes'>
            {overviewData.scopeNotes!.map((note, index) => <div key={index} css={SCOPE_NOTE_STYLE}>{note}</div>)}
          </div>
        </>
      )}
    </div>
  );
};
